import { useMutation, useQueryClient } from "@tanstack/react-query";

import { invalidateToolRegistryQueries } from "./cache-utils";

import { ENDPOINTS } from "@/api/application-api";
import { useAuth } from "@/context/auth-context";
import { handleResponse } from "@/utils/errorResponseHandlers";
import { jsonAuthHeaders } from "@/utils/headers";
import { displayErrorToast } from "@/utils/toast";

interface DuplicateToolRegistryParams {
  workspaceId: number;
  toolId: number;
  targetWorkspaceId: number;
  folderId?: number | null;
}

interface DuplicateToolRegistryResponse {
  success: boolean;
  tool_registry: {
    id: number;
    workspace_id: number;
    name: string;
  };
}

export const useDuplicateToolRegistry = () => {
  const auth = useAuth();
  const userToken = auth?.userToken ?? "";
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      workspaceId,
      toolId,
      targetWorkspaceId,
      folderId,
    }: DuplicateToolRegistryParams): Promise<DuplicateToolRegistryResponse> => {
      const res = await fetch(`${ENDPOINTS.tool_registry.get(workspaceId, toolId)}/duplicate`, {
        method: "POST",
        headers: jsonAuthHeaders(userToken),
        body: JSON.stringify({
          target_workspace_id: targetWorkspaceId,
          folder_id: folderId ?? null,
        }),
      });
      return handleResponse(res);
    },
    onSuccess: (_data, variables) => {
      invalidateToolRegistryQueries(queryClient, variables.targetWorkspaceId);
      if (variables.targetWorkspaceId !== variables.workspaceId) {
        invalidateToolRegistryQueries(queryClient, variables.workspaceId);
      }
    },
    onError: (error: Error) => {
      displayErrorToast(error.message);
    },
  });
};
